import {
  ExternalLink,
  Settings,
  Trophy,
  Gauge,
  ChevronDown,
  Icon,
  User,
  ShoppingCart,
  DollarSign,
  Monitor,
  UsersRound,
} from 'lucide-react';
import { tennisBall } from '@lucide/lab';
import { Link, NavLink, useLocation, useRouteLoaderData } from 'react-router';
import { useAtom } from 'jotai';
import { isSidebarOpenAtom } from '@/stores/ui';
import { cn } from '@/lib/utils';
import { id as routeAppLayout } from '@/layout/app-layout';
import {
  urlCashierMode,
  urlConfig,
  urlControlPanel,
  urlCourtList,
  urlPlayerList,
  urlProductList,
  urlTournamentList,
  urlUserList,
  urlOpenMatches,
  urlSales,
} from '@/urls';
import { UserAvatar } from './user-avatar';
import { useEffect, useMemo } from 'react';
import type { Club } from '@repo/shared-types';
import { Logo } from './logo';

const Footer = () => {
  const { clubs } = useRouteLoaderData(routeAppLayout) as { clubs: Club[] };

  const hasClubs = clubs && clubs.length > 1;
  if (!hasClubs) return null;

  return (
    <Link viewTransition to="/" className="group rounded-md bg-primary p-4">
      <div className="flex items-center justify-between text-white">
        <div>
          <h4 className="text-sm font-medium">Cambiar Club</h4>
          <p className="mt-0.5 text-xs">{clubs.length} clubes disponibles</p>
        </div>
        <ExternalLink className="h-5 w-5" />
      </div>
    </Link>
  );
};

export function Sidebar({ club }: { club: Club }) {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useAtom(isSidebarOpenAtom);

  const items = club
    ? [
        {
          title: 'Panel de control',
          url: urlControlPanel(club.id),
          icon: <Gauge className="h-5 w-5" />,
          options: [],
          requiredFeatures: [],
        },
        {
          title: 'Ventas',
          url: urlSales(club.id),
          icon: <DollarSign className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['pointsOfSale'],
        },
        {
          title: 'Jugadores',
          url: urlPlayerList(club.id),
          icon: <User className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['players'],
        },
        {
          title: 'Canchas',
          url: urlCourtList(club.id),
          icon: <Icon iconNode={tennisBall} className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['pointsOfSale'],
        },
        {
          title: 'Torneos',
          url: urlTournamentList(club.id),
          icon: <Trophy className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['tournaments'],
        },
        {
          title: 'Partidos Abiertos',
          url: urlOpenMatches(club.id),
          icon: <UsersRound className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['openMatches'],
        },
        {
          title: 'Productos',
          url: urlProductList(club.id),
          icon: <ShoppingCart className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['pointsOfSale'],
        },
        {
          title: 'Usuarios',
          url: urlUserList(club.id),
          icon: <User className="h-5 w-5" />,
          options: [],
          requiredFeatures: ['users'],
        },
        {
          title: 'Gestión',
          url: urlConfig(club.id),
          icon: <Settings className="h-5 w-5" />,
          options: [],
          requiredFeatures: [],
        },
      ]
    : [];

  useEffect(() => {
    if (isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [location, setIsSidebarOpen]);

  const filteredItems = useMemo(() => {
    if (!club || !club.features) return [];
    return items.filter((item) =>
      item.requiredFeatures.every(
        (feature) =>
          feature in club.features && club.features[feature as keyof typeof club.features]
      )
    );
  }, [items, club.features]);

  return (
    <>
      {isSidebarOpen && (
        <div className="absolute inset-0 z-50 block lg:hidden">
          <div
            onClick={() => setIsSidebarOpen(false)}
            className="w-full h-full bg-neutral-800 opacity-50"
          ></div>
        </div>
      )}
      <div
        className={cn(
          'min-w-sidebar flex flex-col gap-4 bg-white px-2 py-4 text-gray-800 transition-all duration-300 border-r border-gray-200',
          'absolute lg:relative bottom-0 top-0 z-50 lg:left-auto',
          isSidebarOpen ? 'left-0' : '-left-sidebar'
        )}
      >
        <div className="flex-1">
          <div className="overflow-hidden text-center text-lg font-bold whitespace-nowrap mb-4 flex items-center justify-center">
            <Logo />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            {filteredItems.map((item) => (
              <NavLink
                key={item.title}
                end={item.url === urlControlPanel(club.id)}
                viewTransition
                to={item.url}
                className={({ isActive }) =>
                  cn(
                    'flex h-10 items-center hover:bg-neutral-100 rounded-md px-2',
                    isActive && 'bg-neutral-200'
                  )
                }
              >
                <div className="flex flex-1 items-center gap-2 overflow-hidden whitespace-nowrap justify-start">
                  <span className="overflow-ellipsis">{item.icon}</span>
                  {item.title}
                </div>
                {item.options.length > 0 && <ChevronDown />}
              </NavLink>
            ))}
          </div>
        </div>
        {club.features?.pointsOfSale && (
          <Link
            viewTransition
            to={urlCashierMode(club.id)}
            className="flex items-center gap-2 rounded-lg bg-primary/10 border border-primary/20 px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/20"
          >
            <Monitor className="h-5 w-5" />
            Modo Caja
          </Link>
        )}
        <Footer />
        <UserAvatar />
      </div>
    </>
  );
}
